import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { getBusinessDepartments, getBusinessSites, getUser, removeUserAvatar, updateUserEmail, updateUserMobile, updateUserProfile, uploadUserAvatar, switchBusiness } from './thunk';
import { checkPasscodeLogin } from "slices/co2auth/thunk";
import { showToast } from "Components/Co2Common/Co2ToastContent";
import { stat } from "fs";

interface Co2ProfileState {
    user: UserProps | undefined;
    userBusiness: any | null;
    userDepartment: any | null;
    userSite: any | null;
    userAffiliate: any | null;
    licenseAccess: any;
    mobileAppAccess: boolean;
    loader: boolean;
    currentStep: string;
    error: any;
    trading_sites: any | undefined;
    update_profile: number;
    passcode_expire: number;
    canSwitchCompanies: boolean;
    businessProfile: any;
    defaultSettings: any;
    countryCode: string;
    criticalActions: any;
}

export const initialState: Co2ProfileState = {
    user: undefined,// JSON.parse(sessionStorage.getItem("authUser") || "{}").user,//dddd - undefined
    userBusiness: undefined,
    userDepartment: undefined,
    userSite: undefined,
    userAffiliate: null,
    licenseAccess: undefined,
    mobileAppAccess: false,
    loader: false,
    currentStep: '',
    error: undefined,
    trading_sites: undefined,
    update_profile: 0,
    passcode_expire: 60,
    canSwitchCompanies: false,
    businessProfile: undefined,
    defaultSettings: undefined,
    countryCode: 'gbr',
    criticalActions: undefined,
};

const Co2ProfileSlice = createSlice({
    name: "co2profile",
    initialState,
    reducers: {
        setLoaderAction(state: any) {
            state.loader = true;
            state.error = undefined;
        },
        setCurrentStepAction(state: any, action: any) {
            state.currentStep = action.payload;
            state.error = undefined;
        },
    },
    extraReducers: (builder) => {
        //Check Login Email Verification from Co2Auth slice
        builder.addCase(checkPasscodeLogin.fulfilled, (state: any, action: any) => {
            state.user = action.payload.user;
        });

        //Get User
        builder.addCase(getUser.fulfilled, (state: any, action: any) => {
            state.error = undefined;
            
            // Detect business change
            const oldBusinessId = state.userBusiness?.id || null;
            const newBusinessId = action.payload.userBusiness?.id || null;
            const businessChanged = oldBusinessId !== null && newBusinessId !== null && oldBusinessId !== newBusinessId;
            
            // Update state
            state.user = action.payload.user;
            state.userBusiness = action.payload.userBusiness;
            state.userDepartment = action.payload.userDepartment;
            state.businessProfile = action.payload.businessProfile;
            state.userSite = action.payload.userSite;
            state.userAffiliate = action.payload.userAffiliate;
            state.licenseAccess = action.payload.licenseAccess;
            state.mobileAppAccess = action.payload.mobileAppAccess ?? false;
            state.criticalActions = action.payload.criticalActions;
            state.canSwitchCompanies = action.payload.canSwitchCompanies;
            state.defaultSettings = action.payload.defaultSettings;
            state.countryCode = action.payload.countryCode;
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);
            state.loader = false;
            
            // Log business change for debugging
            if (businessChanged) {
                console.log('[Co2Profile] Business changed detected:', oldBusinessId, '->', newBusinessId);
                // Note: The actual channel switching will be handled by the component
                // that detects this change via useEffect
            }
        });
        builder.addCase(getUser.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Get Business Sites
        builder.addCase(getBusinessSites.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.trading_sites = action.payload.trading_sites;
        });
        builder.addCase(getBusinessSites.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Get Business Departments
        builder.addCase(getBusinessDepartments.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.departments = action.payload.departments;
        });
        builder.addCase(getBusinessDepartments.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Update User Profile
        builder.addCase(updateUserProfile.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.update_profile = state.update_profile + 1;
            state.currentStep = "home";
            //toast.success("User data updated successfully", successToastOptions)
            showToast("User data updated successfully", "success");
        });
        builder.addCase(updateUserProfile.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            //toast.error("An error occured when trying to update user data", { autoClose: 3000 });
            showToast("An error occured when trying to update user data", "error");
        });

        //Upload User Avatar
        builder.addCase(uploadUserAvatar.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.user = action.payload.user;
            state.update_profile = state.update_profile + 1;
            //toast.success("User data updated successfully", successToastOptions);
            showToast("User data updated successfully", "success");
        });
        builder.addCase(uploadUserAvatar.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            //toast.error(action.error.message, { autoClose: 3000 });
            showToast(action.error.message, "error");
        });

        //Remove User Avatar
        builder.addCase(removeUserAvatar.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.user = action.payload.user;
            state.update_profile = state.update_profile + 1;
            //toast.success("User data updated successfully", successToastOptions);
            showToast("User data updated successfully", "success");
        });
        builder.addCase(removeUserAvatar.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            //toast.error(action.error.message, { autoClose: 3000 });
            showToast(action.error.message, "error");
        });

        //Update User Email
        builder.addCase(updateUserEmail.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentStep = "verify_email";
            state.passcode_expire = action.payload.passcode_expire;
        });
        builder.addCase(updateUserEmail.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Update User Mobile
        builder.addCase(updateUserMobile.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            if (action.payload.mobile_phone === "" || action.payload.mobile_phone === null || !action.payload.mobile_phone || action.payload.mobile_phone === "delete") {
                state.user = action.payload.user;
                state.currentStep = "home";
                //toast.success("User data updated successfully", successToastOptions)
                showToast("User data updated successfully", "success");
            } else {
                state.currentStep = "verify_mobile";
                state.passcode_expire = action.payload.passcode_expire;
            }
        });
        builder.addCase(updateUserMobile.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Switch Business
        builder.addCase(switchBusiness.pending, (state: any) => {
            state.loader = true;
            state.error = undefined;
        });
        builder.addCase(switchBusiness.fulfilled, (state: any) => {
            state.loader = false;
            state.error = undefined;
            console.log('[Co2Profile] Business switch completed successfully');
        });
        builder.addCase(switchBusiness.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "Failed to switch business context";
            showToast("Failed to switch business context", "error");
        });
    },
});

export const {
    setLoaderAction, setCurrentStepAction
} = Co2ProfileSlice.actions;

export default Co2ProfileSlice.reducer;
